// State of ETJump's training HUDs, updated once per move from the player
// state, the command and the move's events: jump speeds
// (etj_jump_speeds_v2.cpp), the upmove meter (etj_upmove_meter_data.cpp),
// strafe quality (etj_strafe_quality_v2.cpp) and max speed
// (etj_maxspeed.cpp). Pure data, drawn by ui/hud/etjump.ts.

import { ENTITYNUM_NONE, type PlayerState, type PmoveEvents, type UserCmd } from '../physics/pmove';
import { SURF_SLICK } from '../surfaceflags';
import { inMainAccelZone, snapAccel, snapAngles } from './snaphud';

// --- jump speeds ---

export type SpeedRelation = 'neutral' | 'faster' | 'slower';

export interface JumpSpeed {
  speed: number;
  relation: SpeedRelation;
  /** Upmove meter's full delay of this jump (ms), filled in while jump is held. */
  upmove: number | null;
}

/** MAX_JUMPS */
const MAX_JUMPS = 100;

export class JumpSpeeds {
  readonly jumps: JumpSpeed[] = [];

  /** EV_JUMP with PERS_JUMP_SPEED: a jump at this horizontal speed. */
  add(speed: number): void {
    const prev = this.jumps[this.jumps.length - 1];
    const relation: SpeedRelation = !prev || prev.speed === speed ? 'neutral' : speed > prev.speed ? 'faster' : 'slower';
    this.jumps.push({ speed, relation, upmove: null });
    if (this.jumps.length > MAX_JUMPS) this.jumps.shift();
  }

  reset(): void {
    this.jumps.length = 0;
  }
}

// --- upmove meter ---

export const JumpState = {
  /** in air, not holding jump */
  AIR_NOJUMP: 0,
  /** in air, holding jump */
  AIR_JUMP: 1,
  /** on ground, holding jump */
  GROUND_JUMP: 2,
  /** on ground, not holding jump */
  GROUND_NOJUMP: 3,
  /** in air (after the jump), still holding jump */
  AIR_JUMP_NORELEASE: 4,
} as const;
type JumpStateValue = (typeof JumpState)[keyof typeof JumpState];

/** MAX_UPMOVE_TIME */
const MAX_UPMOVE_TIME = 1000;

/**
 * How long jump was held before landing (preDelay < 0: released before landing,
 * > 0: pressed in the air before landing), after landing (postDelay) and in
 * total for the last jump (fullDelay).
 */
export class UpmoveMeter {
  preDelay = 0;
  postDelay = 0;
  fullDelay = 0;
  jumping = false;
  private jumpState: JumpStateValue = JumpState.GROUND_NOJUMP;
  private lastJumpState: JumpStateValue = JumpState.AIR_NOJUMP;
  private jumpPreGroundTime = 0;
  private groundTouchTime = 0;

  reset(): void {
    this.preDelay = this.postDelay = this.fullDelay = 0;
    this.lastJumpState = JumpState.AIR_NOJUMP;
  }

  /** UpmoveMeterData::runFrame after a move (time = the move's command time). */
  update(ps: PlayerState, cmd: UserCmd, events: PmoveEvents, time: number): void {
    if (ps.pmType === 'noclip' || events.waterlevel > 1 || ps.pmFlags & 4 /* PMF_LADDER */) return;
    const inAir = ps.groundEntityNum === ENTITYNUM_NONE;
    this.jumping = cmd.upmove > 0;
    this.updateJumpState(inAir);
    this.updateValues(time);
    this.lastJumpState = this.jumpState;
  }

  private updateJumpState(inAir: boolean): void {
    const jumping = this.jumping;
    switch (this.lastJumpState) {
      case JumpState.AIR_NOJUMP:
      case JumpState.AIR_JUMP:
        if (inAir) this.jumpState = jumping ? JumpState.AIR_JUMP : JumpState.AIR_NOJUMP;
        else this.jumpState = jumping ? JumpState.GROUND_JUMP : JumpState.GROUND_NOJUMP;
        break;
      case JumpState.GROUND_NOJUMP:
      case JumpState.GROUND_JUMP:
      case JumpState.AIR_JUMP_NORELEASE:
        if (inAir) this.jumpState = jumping ? JumpState.AIR_JUMP_NORELEASE : JumpState.AIR_NOJUMP;
        else this.jumpState = jumping ? JumpState.GROUND_JUMP : JumpState.GROUND_NOJUMP;
        break;
      default:
        this.jumpState = JumpState.GROUND_NOJUMP;
    }
  }

  private updateValues(t: number): void {
    const last = this.lastJumpState;
    switch (this.jumpState) {
      case JumpState.AIR_NOJUMP:
        if (last === JumpState.GROUND_NOJUMP) {
          this.preDelay = this.jumpPreGroundTime - t;
          this.postDelay = 0;
          this.fullDelay = 0;
        } else if (last === JumpState.AIR_JUMP) {
          this.postDelay = 0;
          this.fullDelay = this.preDelay;
        } else if (last === JumpState.AIR_JUMP_NORELEASE) {
          this.fullDelay = this.postDelay;
          if (this.preDelay > 0) this.fullDelay += this.preDelay;
        }
        break;
      case JumpState.AIR_JUMP:
        if (last === JumpState.AIR_NOJUMP) this.jumpPreGroundTime = t;
        this.preDelay = t - this.jumpPreGroundTime;
        break;
      case JumpState.GROUND_JUMP:
        this.groundTouchTime = t;
        break;
      case JumpState.GROUND_NOJUMP:
        if (last === JumpState.AIR_JUMP || last === JumpState.GROUND_JUMP) {
          this.postDelay = 0;
          this.fullDelay = this.preDelay;
        } else if (last === JumpState.AIR_NOJUMP) this.jumpPreGroundTime = t;
        this.preDelay = this.jumpPreGroundTime - t;
        this.groundTouchTime = t;
        break;
      case JumpState.AIR_JUMP_NORELEASE:
        if (last === JumpState.GROUND_NOJUMP) this.preDelay = this.jumpPreGroundTime - t;
        this.postDelay = t - this.groundTouchTime;
        break;
    }
    this.preDelay = Math.max(-MAX_UPMOVE_TIME, Math.min(MAX_UPMOVE_TIME, this.preDelay));
    this.postDelay = Math.min(this.postDelay, MAX_UPMOVE_TIME);
  }
}

// --- strafe quality ---

/**
 * Share of good frames while strafing in the air or on slick: speeding up
 * below ground speed, or (above it) the wish direction in the main
 * acceleration zone, without holding jump. Half a point for a frame that only
 * gained speed.
 */
export class StrafeQuality {
  private score = 0;
  private totalFrames = 0;
  private oldSpeed = 0;
  quality = 0;

  reset(): void {
    this.score = this.totalFrames = this.oldSpeed = 0;
    this.quality = 0;
  }

  get frames(): number {
    return this.totalFrames;
  }

  /** After a move, with the state before it (ground, slick) and the move's acceleration. */
  update(before: Pick<PlayerState, 'groundEntityNum' | 'pmType'>, cmd: UserCmd, events: PmoveEvents, msec: number): void {
    const acc = events.accel;
    if (!acc) return;
    // not strafing / only air or slick movement counts
    if (!cmd.forwardmove && !cmd.rightmove) return;
    if (before.pmType !== 'normal' || events.waterlevel > 1) return;
    if (before.groundEntityNum !== ENTITYNUM_NONE && !(events.groundSurfaceFlags & SURF_SLICK)) return;
    const vf = acc.speed;
    const num = acc.wishspeed - acc.a;
    const optAngle = num >= vf ? 0 : Math.acos(num / vf);
    if (vf < acc.wishspeed) {
      if (vf > this.oldSpeed) {
        // a good frame with no upmove and only forward or only side input
        if (cmd.upmove === 0 && ((cmd.forwardmove !== 0 && cmd.rightmove === 0) || (cmd.forwardmove === 0 && cmd.rightmove !== 0))) this.score++;
        else this.score += 0.5;
      }
    } else {
      const angles = snapAngles(snapAccel(acc.wishspeed, msec));
      if (cmd.upmove === 0 && inMainAccelZone(angles, vf, acc.wishspeed, acc.velAngle, acc.wishAngle, optAngle, cmd)) this.score++;
      else if (vf > this.oldSpeed) this.score += 0.5;
    }
    this.totalFrames++;
    this.quality = (100 * this.score) / this.totalFrames;
    this.oldSpeed = vf;
  }
}

// --- max speed ---

/** The highest speed since the last load; shown (and reset) when the player loads, respawns or finishes. */
export class MaxSpeed {
  max = 0;
  /** Shown value and when it was shown (ms). */
  shown: { speed: number; at: number } | null = null;

  update(ps: PlayerState): void {
    const speed = Math.hypot(ps.velocity[0], ps.velocity[1]);
    if (speed >= this.max) this.max = speed;
  }

  /** EV_LOAD_TELEPORT: show the max speed reached, start over. */
  show(now: number): void {
    this.shown = { speed: this.max, at: now };
    this.max = 0;
  }
}
